import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    gap: 6px;
  }
`;

const Digit = styled.input`
  width: 48px;
  height: 56px;
  text-align: center;
  font-family: ${p => p.theme.fontFamily};
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  background: ${p => p.theme.inputBg};
  border: 1px solid ${p => p.hasError ? p.theme.error : p.theme.inputBorder};
  border-radius: ${p => p.theme.radius};
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  caret-color: ${p => p.theme.accent};

  &:focus {
    border-color: ${p => p.hasError ? p.theme.error : p.theme.borderFocus};
    box-shadow: 0 0 0 3px ${p => p.hasError ? 'rgba(246, 113, 113, 0.1)' : p.theme.ring};
  }

  @media (max-width: 640px) {
    width: 40px;
    height: 48px;
    font-size: 20px;
  }
`;

const ErrorText = styled.div`
  font-family: ${p => p.theme.fontFamily};
  font-size: 13px;
  font-weight: 400;
  color: ${p => p.theme.error};
  line-height: 18px;
  text-align: center;
  margin-bottom: 16px;
`;

export default function OtpInput({ value, onChange, error, disabled }) {
  const inputRefs = useRef([]);
  const digits = value.split('').concat(Array(6 - value.length).fill(''));

  const focusInput = useCallback((index) => {
    if (inputRefs.current[index]) {
      inputRefs.current[index].focus();
    }
  }, []);

  const handleChange = useCallback((index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const newDigits = value.split('').concat(Array(6 - value.length).fill(''));
    newDigits[index] = char;
    const newValue = newDigits.join('').slice(0, 6);
    onChange(newValue);

    if (char && index < 5) {
      focusInput(index + 1);
    }
  }, [value, onChange, focusInput]);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1);
      const newDigits = value.split('').concat(Array(6 - value.length).fill(''));
      newDigits[index - 1] = '';
      onChange(newDigits.join('').replace(/\s+$/, ''));
    }
  }, [value, digits, onChange, focusInput]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    focusInput(Math.min(pasted.length, 5));
  }, [onChange, focusInput]);

  return (
    <>
      <Wrapper>
        {digits.slice(0, 6).map((digit, i) => (
          <Digit
            key={i}
            ref={el => inputRefs.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={disabled}
            hasError={!!error}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            autoFocus={i === 0}
          />
        ))}
      </Wrapper>
      {error && <ErrorText>{error}</ErrorText>}
    </>
  );
}
